/**
 * PedigreeView — classical binary-tree pedigree.
 *
 * Focal person sits at the bottom; two parents above, four grandparents
 * above that, and so on. Only direct ancestors are shown — siblings,
 * cousins, and children are not rendered here (that's what Family Tree
 * is for).
 *
 * Click an ancestor to re-root the pedigree on them. Missing parents
 * render as faint "?" placeholders so the binary shape stays stable at
 * each level, which is the whole point of the pedigree chart.
 */
import { useEffect, useRef, useCallback, useMemo } from 'react'
import * as d3 from 'd3'
import { useTreeStore } from '../../stores/tree.js'
import { extractYear } from '../../utils/date.js'
import './TreeView.css'

const NODE_W = 180
const NODE_H = 60
const H_GAP  = 20
const V_GAP  = 90
const MAX_DEPTH = 6

export default function PedigreeView({ persons, relationships, onPersonClick }) {
  const svgRef = useRef(null)
  const treeStateRef = useRef(null)  // { svg, zoom, bbox }
  const focalPersonId = useTreeStore(s => s.focalPersonId)
  const setFocalPerson = useTreeStore(s => s.setFocalPerson)

  const onPersonClickRef = useRef(onPersonClick)
  const setFocalPersonRef = useRef(setFocalPerson)
  useEffect(() => { onPersonClickRef.current = onPersonClick }, [onPersonClick])
  useEffect(() => { setFocalPersonRef.current = setFocalPerson }, [setFocalPerson])

  // Resolve the root of the pedigree. Fall back to the first person if the
  // focal isn't set yet; pedigree is only meaningful with a root.
  const rootId = focalPersonId || persons[0]?.id

  const pedigree = useMemo(
    () => rootId ? buildPedigree(rootId, persons, relationships, MAX_DEPTH) : null,
    [rootId, persons, relationships]
  )

  const render = useCallback(() => {
    if (!svgRef.current) return
    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const W = svgRef.current.clientWidth || 900
    const H = svgRef.current.clientHeight || 600

    if (!pedigree) {
      svg.append('text')
        .attr('x', W / 2).attr('y', H / 2)
        .attr('text-anchor', 'middle')
        .attr('fill', '#9ca3af').attr('font-size', '14px')
        .text('Pick a person to start a pedigree.')
      return
    }

    // d3.hierarchy with left = mother, right = father (stable for colouring).
    const hierarchy = d3.hierarchy(pedigree, d => [d.mother, d.father].filter(Boolean))

    // d3.tree() lays root at the top; we flip Y at the end to put focal at bottom.
    const layout = d3.tree()
      .nodeSize([NODE_W + H_GAP, NODE_H + V_GAP])
      .separation((a, b) => a.parent === b.parent ? 1 : 1.2)

    layout(hierarchy)

    // Flip vertically: children (ancestors) end up above, root (focal) below.
    const maxY = d3.max(hierarchy.descendants(), d => d.y)
    hierarchy.each(d => { d.y = maxY - d.y })

    const zoom = d3.zoom()
      .scaleExtent([0.25, 2])
      .on('zoom', (event) => g.attr('transform', event.transform))
    svg.call(zoom)

    const g = svg.append('g')

    // Edges: smooth vertical step from parent (above) to child (below)
    const edgeLayer = g.append('g').attr('class', 'edges')
    const link = d3.linkVertical()
      .source(d => ({ x: d.source.x, y: d.source.y + NODE_H / 2 }))
      .target(d => ({ x: d.target.x, y: d.target.y - NODE_H / 2 }))
      .x(d => d.x).y(d => d.y)

    edgeLayer.selectAll('path')
      .data(hierarchy.links())
      .enter().append('path')
      .attr('d', link)
      .attr('fill', 'none')
      .attr('stroke', '#cfc7b6')
      .attr('stroke-width', 1.2)
      .attr('opacity', 0.8)

    // Nodes
    const nodes = g.append('g').attr('class', 'nodes')
      .selectAll('g')
      .data(hierarchy.descendants())
      .enter().append('g')
      .attr('transform', d => `translate(${d.x - NODE_W / 2},${d.y - NODE_H / 2})`)
      .attr('cursor', d => d.data.placeholder ? 'default' : 'pointer')
      .on('click', (_, d) => {
        if (d.data.placeholder) return
        setFocalPersonRef.current(d.data.person.id)
        onPersonClickRef.current(d.data.person.id)
      })

    // Card
    nodes.append('rect')
      .attr('width', NODE_W).attr('height', NODE_H)
      .attr('rx', 6)
      .attr('fill', d => d.data.placeholder ? '#f9f7f2' : '#ffffff')
      .attr('stroke', d => {
        if (d.data.placeholder) return '#e5e0d5'
        return d.data.person.id === focalPersonId ? '#c8a56e' : '#e5e0d5'
      })
      .attr('stroke-width', d => d.data.person?.id === focalPersonId ? 2 : 1)
      .attr('stroke-dasharray', d => d.data.placeholder ? '3,3' : null)

    // Gender accent bar on the left edge for real persons
    nodes.filter(d => !d.data.placeholder).append('rect')
      .attr('width', 3).attr('height', NODE_H)
      .attr('rx', 3)
      .attr('fill', d => {
        const g = d.data.person.gender
        return g === 'male' ? '#3b6b5a' : g === 'female' ? '#c8a56e' : '#9ca3af'
      })

    // Name (line 1)
    nodes.append('text')
      .attr('x', 12).attr('y', 22)
      .attr('font-size', '13px').attr('font-weight', '600')
      .attr('fill', d => d.data.placeholder ? '#9ca3af' : '#1a1a1a')
      .text(d => {
        if (d.data.placeholder) return 'Unknown ancestor'
        const p = d.data.person
        const first = p.preferred_name || (p.first_name ? p.first_name.split(' ')[0] : '')
        return truncate(first || 'Unknown', 22)
      })

    // Surname (line 2)
    nodes.filter(d => !d.data.placeholder && d.data.person.last_name).append('text')
      .attr('x', 12).attr('y', 37)
      .attr('font-size', '11.5px').attr('font-weight', '500')
      .attr('fill', '#374151')
      .text(d => truncate(d.data.person.last_name, 24))

    // Years (line 3)
    nodes.filter(d => !d.data.placeholder).append('text')
      .attr('x', 12).attr('y', 52)
      .attr('font-size', '10px').attr('fill', '#6b7280')
      .text(d => {
        const p = d.data.person
        const by = extractYear(p.birth_date)
        const dy = extractYear(p.death_date)
        return by ? (dy ? `${by} – ${dy}` : `b. ${by}`) : ''
      })

    // Add-parent affordance on placeholder cards
    nodes.filter(d => d.data.placeholder).append('text')
      .attr('x', NODE_W / 2).attr('y', 37)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px').attr('fill', '#9ca3af')
      .text('—')

    // Initial fit-to-view
    const bbox = g.node().getBBox()
    const scale = Math.min(0.95, (W - 40) / bbox.width, (H - 40) / bbox.height)
    const tx = (W - bbox.width * scale) / 2 - bbox.x * scale
    const ty = (H - bbox.height * scale) / 2 - bbox.y * scale
    svg.call(zoom.transform, d3.zoomIdentity.translate(tx, ty).scale(scale))

    // Stash for the recenter button
    treeStateRef.current = { svg, zoom, bbox, W, H }
  }, [pedigree, focalPersonId])

  const handleRecenter = useCallback(() => {
    const st = treeStateRef.current
    if (!st) return
    const { svg, zoom, bbox, W, H } = st
    const scale = Math.min(0.95, (W - 40) / bbox.width, (H - 40) / bbox.height)
    const tx = (W - bbox.width * scale) / 2 - bbox.x * scale
    const ty = (H - bbox.height * scale) / 2 - bbox.y * scale
    svg.transition().duration(450)
      .call(zoom.transform, d3.zoomIdentity.translate(tx, ty).scale(scale))
  }, [])

  useEffect(() => {
    render()
    const ro = new ResizeObserver(render)
    if (svgRef.current) ro.observe(svgRef.current.parentElement)
    return () => ro.disconnect()
  }, [render])

  return (
    <div className="tree-view">
      <svg ref={svgRef} width="100%" height="100%" />
      {pedigree && (
        <button
          type="button"
          className="tree-view__recenter"
          onClick={handleRecenter}
          title="Fit pedigree to view"
        >
          Fit to view
        </button>
      )}
      {pedigree && (
        <div className="tree-view__legend">
          <span className="legend-item">Click any ancestor to re-root the pedigree</span>
        </div>
      )}
    </div>
  )
}

// ─── Pedigree builder ──────────────────────────────────────────────────────

function buildPedigree(focalId, persons, relationships, maxDepth) {
  const byId = new Map(persons.map(p => [p.id, p]))
  const parentEdges = relationships.filter(r => r.type === 'parent_child')

  function parentsOf(childId) {
    return parentEdges
      .filter(r => r.person_b_id === childId)
      .map(r => byId.get(r.person_a_id))
      .filter(Boolean)
  }

  function build(personId, depth, seen) {
    const p = byId.get(personId)
    if (!p) return null
    if (seen.has(personId)) return { person: p, mother: null, father: null }
    const next = new Set(seen).add(personId)

    const ps = parentsOf(personId)
    const mother = ps.find(x => x.gender === 'female') || null
    const father = ps.find(x => x.gender === 'male') || null
    // If gender isn't set we still want two slots. Pick in a stable order.
    const others = ps.filter(x => x !== mother && x !== father)
    const m = mother || others[0] || null
    const f = father || others.find(x => x !== m) || null

    const node = { person: p, placeholder: false, mother: null, father: null }

    if (depth < maxDepth) {
      node.mother = m
        ? build(m.id, depth + 1, next)
        : { placeholder: true, mother: null, father: null }
      node.father = f
        ? build(f.id, depth + 1, next)
        : { placeholder: true, mother: null, father: null }
    }
    return node
  }

  return build(focalId, 0, new Set())
}

function truncate(str, len) {
  return str.length > len ? str.slice(0, len - 1) + '…' : str
}
